import { useMemo } from 'react'
import Stack from '@mui/material/Stack'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { PieChart, PieValueType } from '@mui/x-charts-pro'
import map from 'lodash/map'

import {
  formatDateTime,
  useActivityOptions,
  useLaboratoriesOptions,
  useSponsorshipStatusOptions,
  useSponsorshipTypeOptions,
} from '../../../../utils'
import { Events, SponsorshipStats } from '../../../../types'
import { Dot } from './Dot'

interface Props {
  statistics: SponsorshipStats | null | undefined
  event: Events<true> | null | undefined
}

export default function AdminSponsorshipDetails({ statistics, event }: Props) {
  const { t } = useTranslation('admin')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))

  const laboratoryOptions = useLaboratoriesOptions()
  const sponsorshipTypeOptions = useSponsorshipTypeOptions()
  const statusOptions = useSponsorshipStatusOptions(t)
  const activityOptions = useActivityOptions(t)

  const data = useMemo(() => {
    const laboratories = map(
      statistics?.laboratory_stats ?? [],
      value =>
        ({
          id: value.laboratory.id,
          value: value.count,
          label: `${laboratoryOptions.find(el => el.value === value.laboratory.id)?.label ?? value.laboratory.name}: ${value.count} / ${value.quota}`,
        }) as PieValueType
    )
    const sponsorshipTypes = map(
      statistics?.sponsorship_type_stats ?? [],
      value =>
        ({
          id: value.type.id,
          value: value.count,
          label: `${sponsorshipTypeOptions.find(el => el.value === value.type.id)?.label ?? value.type.name}: ${value.count} / ${value.quota}`,
        }) as PieValueType
    )
    const status = map(
      statistics?.status_stats ?? [],
      value =>
        ({
          id: value.status,
          value: value.count,
          label: `${statusOptions.find(el => el.value === value.status)?.label ?? value.status}: ${value.count}`,
          color: statusOptions.find(el => el.value === value.status)?.color,
        }) as PieValueType
    )
    const activities = map(
      statistics?.activity_stats ?? [],
      value =>
        ({
          id: value.activity,
          value: value.count,
          label: `${activityOptions.find(el => el.value === value.activity)?.label ?? value.activity}: ${value.count}`,
        }) as PieValueType
    )
    const jobTitles = map(
      statistics?.job_title_stats ?? [],
      value =>
        ({
          id: value.job_title.value,
          value: value.count,
          label: `${value.job_title.label}: ${value.count}`,
        }) as PieValueType
    ).filter(value => value.value > 0)

    return {
      laboratories,
      sponsorshipTypes,
      status,
      activities,
      jobTitles,
    }
  }, [
    activityOptions,
    laboratoryOptions,
    sponsorshipTypeOptions,
    statusOptions,
    statistics?.activity_stats,
    statistics?.job_title_stats,
    statistics?.laboratory_stats,
    statistics?.sponsorship_type_stats,
    statistics?.status_stats,
  ])

  return (
    <Stack direction="column">
      <Stack direction={{ md: 'column', lg: 'row' }}>
        <Stack direction="column" flex="1 0">
          <Stack direction="column">
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Total p. charge')} :</h6>
              <Dot>{statistics?.total_sponsorships ?? 'NC'}</Dot>
            </Stack>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Début p. charge')} :</h6>
              <span>{event?.sponsorship_request_start ? formatDateTime(event.start) : 'NC'}</span>
            </Stack>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Fin p. charge')} :</h6>
              <span>{event?.sponsorship_request_end ? formatDateTime(event.end) : 'NC'}</span>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction={{ md: 'column', lg: 'row' }} flex="2 0">
          <PieChart
            series={[
              {
                data: data.laboratories,
              },
            ]}
            slotProps={{
              legend: {
                direction: 'column',
                position: {
                  vertical: 'top',
                  horizontal: isMobile ? 'left' : 'right',
                },
                itemMarkWidth: 2,
                itemMarkHeight: 20,
                markGap: 5,
                itemGap: 5,
              },
            }}
            margin={
              isMobile
                ? { top: 0, right: 0, bottom: 0, left: 200 }
                : { top: 0, right: 200, bottom: 0, left: 0 }
            }
            height={150}
          />
          <PieChart
            series={[
              {
                data: data.sponsorshipTypes,
              },
            ]}
            slotProps={{
              legend: {
                direction: 'column',
                position: {
                  vertical: 'top',
                  horizontal: isMobile ? 'left' : 'right',
                },
                itemMarkWidth: 2,
                itemMarkHeight: 20,
                markGap: 5,
                itemGap: 5,
              },
            }}
            margin={
              isMobile
                ? { top: 0, right: 0, bottom: 0, left: 200 }
                : { top: 0, right: 200, bottom: 0, left: 0 }
            }
            height={150}
          />
        </Stack>
      </Stack>
      <Stack direction={{ md: 'column', lg: 'row' }}>
        <PieChart
          series={[
            {
              data: data.status,
            },
          ]}
          slotProps={{
            legend: {
              direction: 'column',
              position: {
                vertical: 'top',
                horizontal: isMobile ? 'left' : 'right',
              },
              itemMarkWidth: 2,
              itemMarkHeight: 20,
              markGap: 5,
              itemGap: 5,
            },
          }}
          margin={
            isMobile
              ? { top: 0, right: 0, bottom: 0, left: 200 }
              : { top: 0, right: 200, bottom: 0, left: 0 }
          }
          height={150}
        />
        <PieChart
          series={[
            {
              data: data.activities,
            },
          ]}
          slotProps={{
            legend: {
              direction: 'column',
              position: {
                vertical: 'top',
                horizontal: isMobile ? 'left' : 'right',
              },
              itemMarkWidth: 2,
              itemMarkHeight: 20,
              markGap: 5,
              itemGap: 5,
            },
          }}
          margin={
            isMobile
              ? { top: 0, right: 0, bottom: 0, left: 200 }
              : { top: 0, right: 200, bottom: 0, left: 0 }
          }
          height={150}
        />
        <PieChart
          series={[
            {
              data: data.jobTitles,
            },
          ]}
          slotProps={{
            legend: {
              direction: 'column',
              position: {
                vertical: 'top',
                horizontal: isMobile ? 'left' : 'right',
              },
              itemMarkWidth: 2,
              itemMarkHeight: 20,
              markGap: 5,
              itemGap: 5,
            },
          }}
          margin={
            isMobile
              ? { top: 0, right: 0, bottom: 0, left: 200 }
              : { top: 0, right: 200, bottom: 0, left: 0 }
          }
          height={150}
        />
      </Stack>
    </Stack>
  )
}
